// Legacy import facts (repos/migration/plan.md §4.5, entity-transform-map Stages 12–13; commerce migration-notes §1).
// One `*Imported` event per legacy aggregate, written by `tools/migrate` with `rulesVersion: 'legacy'`: the stored
// legacy totals are authoritative and never recomputed. Values legacy never stored (per-line shares of the cart
// discount, the cash discount and tax) are allocated once at import and marked, so every report row adds up.
// These facts are history: the live aggregates in core never fold them, so `deviceConsumed` is false until a device
// can read history (refunds of imported orders arrive with the refunds unit).
import { z } from 'zod';
import { defineEvent } from '../../define/event.ts';
import { additive } from '../../define/upcast.ts';
import { OnlineOrderStatus } from '../online/orders.ts';
import { LineModifierGroup, OrderCustomer } from '../orders/index.ts';
import {
  BreakPolicyId, CashMovementId, UserId, CheckId, CustomerId, DeviceId, DiningOptionId, EmployeeId, EpochMs, GiftCardId, Integer, LocalDate, LocationId, Milli, Money, OrderId, OrderLineId, PrinterId, ProductId, Rate, TableId, TenderId, TillSessionId,
  IsoInstant, TimecardId, VariantId,
} from '../../primitives/codecs.ts';

/** The legacy row an imported fact came from (integer id, kept for support and for `sync.legacy_refs`). */
const LegacyId = z.int().nonnegative();
/** Data-quality markers the import attached (commerce migration-notes §5): never a change of money, always reported. */
const Flags = z.array(z.string().regex(/^[a-z][a-z0-9_]*$/)).max(32);

const history = { deviceConsumed: false, merge: 'idempotent_set' } as const;

/**
 * v13 (lane D2, carry-over tabs): what staff entered for a discount, from the legacy row's `type` and `amount`: a
 * percent (legacy keeps a whole percent, 10 = 10 %) as a Rate, or an amount in cents. On a line the amount is per unit,
 * as legacy's register applies it (`getSingleItemDiscountAmount` on the unit price plus modifiers).
 */
const EnteredBasis = z.union([z.strictObject({ percent: Rate }), z.strictObject({ amount: Money })]).meta({ id: 'ImportedEnteredBasis' });

const ImportedLineDiscount = z
  .object({
    ruleSource: z.enum(['auto', 'preset', 'custom', 'unknown_legacy']),
    /** The imported discount rule, when the legacy row named one that resolves in the same location. */
    discountRuleId: z.uuid({ version: 'v7' }).nullable(),
    basis: z.enum(['percent', 'amount']),
    /** Legacy `order_item_discounts.discount` as stored: the discount legacy computed per unit, in cents (not what was entered). */
    entered: z.string().regex(/^-?\d+$/),
    amount: Money,
    reason: z.string().max(255).nullable(),
    /** v13: what staff entered (`type` and `amount`). Absent before v13. */
    enteredBasis: EnteredBasis.optional(),
  })
  .meta({ id: 'ImportedLineDiscount' });

/**
 * v10: the scheduled price adjustment the line was rung under (legacy `order_item_price_adjustments`, A80), as legacy
 * noted it: the rule, markup or markdown or fixed amount, and what was entered (cents, or a percent). Informational, as
 * in legacy, which never reads it back for a closed order: `unitPrice` and `gross` already hold the adjusted price, so
 * nothing here moves money.
 */
const ImportedLinePriceAdjustment = z
  .object({
    legacyRuleId: LegacyId,
    /** The imported price-adjustment rule; null when legacy deleted the rule (deleted rules are not imported). */
    discountRuleId: z.uuid({ version: 'v7' }).nullable(),
    kind: z.enum(['markup', 'markdown', 'fixed_amount']),
    basis: z.union([z.strictObject({ percent: Rate }), z.strictObject({ amount: Money })]),
  })
  .meta({ id: 'ImportedLinePriceAdjustment' });

/**
 * v14: one legacy item void (`voided_order_items` joined to its clone `order_items` row, `order_id` 0), as legacy's
 * Deletions report (VoidsController, AdjustmentsDeletions.vue) reads it.
 */
const ImportedLineVoid = z
  .object({
    /** `voided_order_items.id`. */
    legacyId: LegacyId,
    /** The clone's `order_items.quantity` × 1000, its sign as stored (legacy shows it as stored), so signed. */
    qtyMilli: Integer,
    /** The Deletions report's Item Amount: the clone's `subtotal + tax`, no discount taken off. */
    amount: Money,
    reason: z.string().max(255).nullable(),
    /** The clone's product, when the import brought it over. */
    productId: ProductId.nullable(),
    /** Legacy's `clone.product?.name ?? 'Unknown Product'` at import (a soft-deleted product reads 'Unknown Product'). */
    productName: z.string().max(255),
    /** `voided_order_items.voided_by_employee_id`, for audit (legacy's row shows the order's employee). */
    voidedByEmployeeId: EmployeeId.nullable(),
    /** `voided_order_items.voided_at`, for audit (legacy dates the row by its order). */
    voidedAt: EpochMs.nullable(),
  })
  .meta({ id: 'ImportedLineVoid' });

const ImportedLine = z
  .object({
    lineId: OrderLineId,
    legacyLineId: LegacyId,
    /** null for legacy open/misc lines with no product (`product_id = 0`). */
    productId: ProductId.nullable(),
    /**
     * The variant rung (`order_items.price_id` of a product sold at more than one price); null: the product has one
     * price, or the price was deleted before the import. Absent on facts written before v5.
     */
    variantId: VariantId.nullable().optional(),
    name: z.string().max(255),
    productKind: z.enum(['normal', 'unit', 'custom', 'misc', 'gift', 'house']),
    taxTreatment: z.enum(['exclusive', 'inclusive', 'exempt']),
    qtyMilli: Milli,
    voidedQtyMilli: Milli,
    unitPrice: Money,
    /** `order_items.subtotal`: the line as rung (live quantity). */
    gross: Money,
    /** `order_items.disc`: line-scope discounts, positive. */
    lineDiscount: Money,
    /** Allocated at import: the line's share of the cart discount, of the True Margin cash discount and of the service charge. */
    cartDiscountShare: Money,
    cashDiscountShare: Money,
    serviceChargeShare: Money,
    tax: Money,
    /** True when `tax` was allocated from `orders.tax` because the stored line taxes did not add up to it. */
    taxAllocated: z.boolean(),
    /**
     * v9: `order_items.tax` as legacy stored it, on a line whose `tax` was allocated (`taxAllocated`) and differs from it.
     * Legacy's Overview takes a gift-card load's or house-account payment's stored tax off its Tax even when the order's
     * tax never held it (an $8.00 House Account line discounted 100% keeps its $0.62, bug B375). Absent: the stored tax is
     * `tax`, or the fact was written before v9.
     */
    storedTax: Money.optional(),
    /** What voids took off the line (the void clones legacy detached with `order_id = 0`). */
    voidedValue: Money,
    voidReason: z.string().max(255).nullable(),
    discounts: z.array(ImportedLineDiscount).max(20),
    /**
     * v6: the modifiers the line was rung with (legacy `order_item_modifiers` and their `order_item_modifier_items`), in
     * the live line's shape, so an imported order shows them as a live one does. Informational: `gross` already holds
     * their price. Absent on facts written before v6; a voided clone line has none.
     */
    modifiers: z.array(LineModifierGroup).max(50).optional(),
    /**
     * v8: the check the line is on, on an order split by item (legacy `order_items.ticket`, the check's index). Absent
     * on an unsplit or evenly split order (an even split shares every line), and on facts written before v8.
     */
    checkId: CheckId.optional(),
    /** v10: the price adjustment the line was rung under, when legacy noted one. Absent: none, or a fact before v10. */
    priceAdjustment: ImportedLinePriceAdjustment.optional(),
    /**
     * v11: a misc line's ticked printers (legacy `misc_order_item_printer`), in LineAdded v8's meaning: the printers a
     * reprint sends it to, none ticked included. Only printers the import brought over are named (a deleted one is
     * dropped, flagged). Absent: not a misc line, a misc line legacy recorded no printers for (rung before the card
     * offered them: the printers preselected for misc items), or a fact before v11.
     */
    printerIds: z.array(PrinterId).max(50).optional(),
    /**
     * v14 (lane D5, Adjustments › Deletions): each legacy void cut from this line (or standing as it), one per
     * `voided_order_items` row, as legacy's Deletions report lists it. Absent: none voided, or a fact before v14.
     */
    voids: z.array(ImportedLineVoid).max(200).optional(),
    /**
     * v15: legacy rang the line's keyed price as the cash price (`order_items.cash_price_toggle`, the "With Cash Discount"
     * toggle on True Margin custom and misc items), as LineAdded's `cashPriceEntered`. Informational: `unitPrice` and
     * `gross` already hold the listed price. Present only when true; absent: rung at the listed price, or a fact before v15.
     */
    cashPriceEntered: z.literal(true).optional(),
    /**
     * v17 (lane M round 29, with D5; B458): legacy's stored `order_items.subtotal` where it differs from `gross`. Legacy's
     * API stored a half-and-half line with its halves summed unrounded and rounded once, while the register charged each
     * half rounded (`gross`, which adds up to the order's subtotal). Legacy's Item, Category and Department Sales read the
     * stored value. Present only when it differs; absent: the stored line is `gross`, or a fact before v17.
     */
    storedSubtotal: Money.optional(),
    /**
     * v18 (lane M round 29, with lane C): on an order imported open, the units legacy's register had already sent to the
     * kitchen (`order_items.printed_quantity`, null as 0, at most the line's quantity), which a carry-over keeps so its
     * first send fires only the rest. Absent: the order was not open, or a fact before v18.
     */
    sentQtyMilli: Milli.optional(),
    /**
     * v19 (lane D5 round 44): legacy stored no name on the line (`order_items.name` null or empty: every online order's
     * lines), so `name` is its product's, as legacy's Dashboard resolves it (`resolveItemName`). Legacy's item reports
     * compare the stored name, so such a line is never the "Gift Card" line they leave out (B314). Present only when true.
     */
    nameFromProduct: z.literal(true).optional(),
    /**
     * v23 (lane X, #2639): legacy stored the name '0' on the line (`order_items.name`), which its reports read as empty
     * (`?:`, so `name` is its product's and `nameFromProduct` is set) but its customer page's Favorite Items groups as a
     * row of its own named '0' (`GROUP BY order_items.name`). Present only when true.
     */
    storedNameZero: z.literal(true).optional(),
    /**
     * v24 (lane RP, B265 review B2): legacy took the line out of the order ("Item removed from order",
     * `markNoExistingOrderItemsAsObsolete`: the row itself moved to `order_id = 0`), and no live row of its product and
     * price was left for the void to land on, so the import stands it as a fully voided line of its own. Legacy's order
     * reads never see it, so Item Sales' variant rows don't take their cash discount from it. Present only when true;
     * absent: a line legacy kept in the order, or a fact before v24.
     */
    removedFromOrder: z.literal(true).optional(),
  })
  .meta({ id: 'ImportedLine' });

/** OrderImported v1: the snapshot before it carried the dining option. */
const OrderImportedV1 = z.object({
  orderId: OrderId,
  locationId: LocationId,
  legacyOrderId: LegacyId,
  orderNumberDisplay: z.string().max(32).nullable(),
  businessDate: LocalDate,
  openedAt: EpochMs,
  closedAt: EpochMs.nullable(),
  lifecycle: z.enum(['open', 'paid', 'closed', 'voided', 'abandoned']),
  lifecycleCause: z.enum(['legacy_void', 'legacy_eod_void', 'legacy_stale']).nullable(),
  channel: z.enum(['pos', 'online', 'app']),
  ownerEmployeeId: EmployeeId.nullable(),
  tillSessionId: TillSessionId.nullable(),
  isBarTab: z.boolean(),
  storedTotals: z.object({
    subtotal: Money, cartDiscount: Money, serviceCharge: Money, tax: Money, total: Money, tip: Money, paid: Money, cashDiscount: Money, surcharge: Money,
  }),
  cartDiscount: z
    .object({ ruleSource: z.enum(['preset', 'custom', 'unknown_legacy']), basis: z.enum(['percent', 'amount']), entered: z.string().regex(/^-?\d+$/).nullable(), reason: z.string().max(255).nullable() })
    .nullable(),
  lines: z.array(ImportedLine).max(2_000),
  voided: z.object({ reason: z.string().max(255).nullable(), employeeId: EmployeeId.nullable() }).nullable(),
  flags: Flags,
});

/** OrderImported v2: the snapshot before its cart discount named the preset rule it came from. */
const OrderImportedV2 = OrderImportedV1.extend({
  /** The order's dining option (legacy `orders.dining_option_id`), imported with the location's dining options; null = none. */
  diningOptionId: DiningOptionId.nullable(),
});

const ImportedCartDiscount = z.object({
  ruleSource: z.enum(['preset', 'custom', 'unknown_legacy']),
  /** The imported preset rule a cart discount was rung from, when the legacy row named one that resolves in the same location (RC-R2). */
  discountRuleId: z.uuid({ version: 'v7' }).nullable(),
  basis: z.enum(['percent', 'amount']),
  entered: z.string().regex(/^-?\d+$/).nullable(),
  reason: z.string().max(255).nullable(),
  /**
   * `order_discounts.amount` as stored: the percent on a percent discount, cents on an amount discount. Legacy's
   * reports read it on a location not on True Margin (a percent recomputed off the subtotal, B144). null without a
   * legacy row; absent before v5.
   */
  amount: z.string().regex(/^-?\d+$/).nullable().optional(),
  /** v13: what staff entered, from `order_discounts.type` and `.amount`. Absent without a legacy row, and before v13. */
  enteredBasis: EnteredBasis.optional(),
});

/** OrderImported v3: the snapshot before it carried the pricing program and sales tax rate it was sold under. */
const OrderImportedV3 = OrderImportedV2.extend({ cartDiscount: ImportedCartDiscount.nullable() });

/**
 * The pricing program and default sales tax rate in force at the location on the order's business date, from the
 * owner-signed timeline (migration plan 4.8, RC-C11). Legacy kept only the current ones; null = not stamped (v3 facts).
 */
const ImportedPricing = z
  .object({ program: z.enum(['traditional', 'non_cash_adjustment', 'true_margin']), taxRate: Rate })
  .meta({ id: 'ImportedPricing' });

/**
 * v7: the order's tab (legacy `tabs`, through `orders.tab_id` as legacy's `Order::tab()` reads it): the name staff gave
 * the order, null when the tab has none (a table seated without a name), and whether that name is left off customer
 * receipts ("Hide name on customer receipt", `tabs.receipt_print_order`; A54: then no name prints on any customer
 * receipt). Absent when legacy shows no tab: none linked, or the link names a tab that is deleted or no longer stored.
 */
const ImportedTab = z
  .object({ legacyTabId: LegacyId, name: z.string().min(1).max(255).nullable(), hideNameOnReceipt: z.boolean() })
  .meta({ id: 'ImportedTab' });

/**
 * v8: how a split order's checks divide it (legacy `order_splits` {type, count}, with `order_items.ticket` and
 * `tenders.t` naming each line's and payment's check by index), in the live `ChecksSplit` shape: `even` shares every
 * line across the checks, `by_item` puts each line on one. Absent on an order legacy did not split, or split into one check.
 */
const ImportedChecks = z
  .object({ mode: z.enum(['even', 'by_item']), checks: z.array(z.object({ checkId: CheckId, checkNumber: z.int().positive() })).min(2).max(64) })
  .meta({ id: 'ImportedChecks' });

/**
 * v12: the online order behind an imported order (legacy `online_orders` and its `online_order_events`), reviewed by
 * lane D3: its status, whether it was a QR table pay, its ready estimate, and every status change in order as legacy
 * recorded it (`accepted` included). The customer is a reference to the imported customer only (legacy
 * `orders.customer_id`); no name, email or phone is carried, so a guest's name is not in the import (a known
 * difference). The payment token, idempotency key, gateway invoice and DoorDash Drive columns are not carried.
 */
const ImportedOnline = z
  .object({
    legacyOnlineOrderId: LegacyId,
    status: OnlineOrderStatus,
    qrTablePay: z.boolean(),
    estimateReadyAt: EpochMs.nullable(),
    timeline: z.array(z.object({ status: OnlineOrderStatus, at: EpochMs })).max(20),
    customerId: CustomerId.nullable(),
  })
  .meta({ id: 'ImportedOnline' });

/** The fields `OrderImported` v13 added: a reader of v12 takes the order without its gratuity rate and entered bases. */
const orderAsV12 = (newer: unknown): unknown => {
  const o = Object.fromEntries(Object.entries(newer as Record<string, unknown>).filter(([key]) => key !== 'gratuityRate'));
  const strip = (x: unknown): unknown => (x !== null && typeof x === 'object' ? Object.fromEntries(Object.entries(x as Record<string, unknown>).filter(([key]) => key !== 'enteredBasis')) : x);
  const cart = o['cartDiscount'];
  const lines = o['lines'];
  return {
    ...o,
    ...(cart === undefined ? {} : { cartDiscount: strip(cart) }),
    ...(Array.isArray(lines) ? { lines: lines.map((l: unknown) => {
      const line = l as Record<string, unknown>;
      return Array.isArray(line['discounts']) ? { ...line, discounts: line['discounts'].map(strip) } : line;
    }) } : {}),
  };
};

/** The field `OrderImported` v24 added to each line: a reader of v23 takes the lines without it. */
const orderAsV23 = (newer: unknown): unknown => {
  const o = newer as { lines?: unknown };
  if (!Array.isArray(o.lines)) return newer;
  return { ...o, lines: o.lines.map((l: unknown) => Object.fromEntries(Object.entries(l as Record<string, unknown>).filter(([key]) => key !== 'removedFromOrder'))) };
};

/** The field `OrderImported` v23 added to each line: a reader of v22 takes the lines without it. */
const orderAsV22 = (newer: unknown): unknown => {
  const o = newer as { lines?: unknown };
  if (!Array.isArray(o.lines)) return newer;
  return { ...o, lines: o.lines.map((l: unknown) => Object.fromEntries(Object.entries(l as Record<string, unknown>).filter(([key]) => key !== 'storedNameZero'))) };
};

/** The field `OrderImported` v22 added: a reader of v21 takes the order without its customer reference (B1656). */
const orderAsV21 = (newer: unknown): unknown => Object.fromEntries(Object.entries(newer as Record<string, unknown>).filter(([key]) => key !== 'customerId'));

/** The field `OrderImported` v21 added: a reader of v20 takes the order without its auto-gratuity status (read as none). */
const orderAsV20 = (newer: unknown): unknown => Object.fromEntries(Object.entries(newer as Record<string, unknown>).filter(([key]) => key !== 'autoGratuityStatus'));

/** The field `OrderImported` v20 added: a reader of v19 takes the order without its customer. */
const orderAsV19 = (newer: unknown): unknown => Object.fromEntries(Object.entries(newer as Record<string, unknown>).filter(([key]) => key !== 'customer'));

/** The field `OrderImported` v19 added to each line: a reader of v18 takes the lines without it. */
const orderAsV18 = (newer: unknown): unknown => {
  const o = newer as { lines?: unknown };
  if (!Array.isArray(o.lines)) return newer;
  return { ...o, lines: o.lines.map((l: unknown) => Object.fromEntries(Object.entries(l as Record<string, unknown>).filter(([key]) => key !== 'nameFromProduct'))) };
};

/** The field `OrderImported` v18 added to each line: a reader of v17 takes the lines without it. */
const orderAsV17 = (newer: unknown): unknown => {
  const o = newer as { lines?: unknown };
  if (!Array.isArray(o.lines)) return newer;
  return { ...o, lines: o.lines.map((l: unknown) => Object.fromEntries(Object.entries(l as Record<string, unknown>).filter(([key]) => key !== 'sentQtyMilli'))) };
};

/** The field `OrderImported` v17 added to each line: a reader of v16 takes the lines without it. */
const orderAsV16 = (newer: unknown): unknown => {
  const o = newer as { lines?: unknown };
  if (!Array.isArray(o.lines)) return newer;
  return { ...o, lines: o.lines.map((l: unknown) => Object.fromEntries(Object.entries(l as Record<string, unknown>).filter(([key]) => key !== 'storedSubtotal'))) };
};

/** The field `OrderImported` v16 added: a reader of v15 takes the order without its table. */
const orderAsV15 = (newer: unknown): unknown => Object.fromEntries(Object.entries(newer as Record<string, unknown>).filter(([key]) => key !== 'tableId'));

/** The field `OrderImported` v15 added to each line: a reader of v14 takes the lines without it. */
const orderAsV14 = (newer: unknown): unknown => {
  const o = newer as { lines?: unknown };
  if (!Array.isArray(o.lines)) return newer;
  return { ...o, lines: o.lines.map((l: unknown) => Object.fromEntries(Object.entries(l as Record<string, unknown>).filter(([key]) => key !== 'cashPriceEntered'))) };
};

/** The field `OrderImported` v14 added to each line: a reader of v13 takes the lines without it. */
const orderAsV13 = (newer: unknown): unknown => {
  const o = newer as { lines?: unknown };
  if (!Array.isArray(o.lines)) return newer;
  return { ...o, lines: o.lines.map((l: unknown) => Object.fromEntries(Object.entries(l as Record<string, unknown>).filter(([key]) => key !== 'voids'))) };
};

/** The field `OrderImported` v12 added: a reader of v11 takes the order without its online record. */
const orderAsV11 = (newer: unknown): unknown => Object.fromEntries(Object.entries(newer as Record<string, unknown>).filter(([key]) => key !== 'online'));

/** The field `OrderImported` v11 added to each line: a reader of v10 takes the lines without it. */
const orderAsV10 = (newer: unknown): unknown => {
  const o = newer as { lines?: unknown };
  if (!Array.isArray(o.lines)) return newer;
  return { ...o, lines: o.lines.map((l: unknown) => Object.fromEntries(Object.entries(l as Record<string, unknown>).filter(([key]) => key !== 'printerIds'))) };
};

/** The field `OrderImported` v10 added to each line: a reader of v9 takes the lines without it. */
const orderAsV9 = (newer: unknown): unknown => {
  const o = newer as { lines?: unknown };
  if (!Array.isArray(o.lines)) return newer;
  return { ...o, lines: o.lines.map((l: unknown) => Object.fromEntries(Object.entries(l as Record<string, unknown>).filter(([key]) => key !== 'priceAdjustment'))) };
};

/** The field `OrderImported` v9 added to each line: a reader of v8 takes the lines without it. */
const orderAsV8 = (newer: unknown): unknown => {
  const o = newer as { lines?: unknown };
  if (!Array.isArray(o.lines)) return newer;
  return { ...o, lines: o.lines.map((l: unknown) => Object.fromEntries(Object.entries(l as Record<string, unknown>).filter(([key]) => key !== 'storedTax'))) };
};

/** The fields `OrderImported` v8 added: a reader of v7 takes the order without its checks, and its lines without theirs. */
const orderAsV7 = (newer: unknown): unknown => {
  const o = Object.fromEntries(Object.entries(newer as Record<string, unknown>).filter(([key]) => key !== 'checks'));
  if (!Array.isArray(o['lines'])) return o;
  return { ...o, lines: o['lines'].map((l: unknown) => Object.fromEntries(Object.entries(l as Record<string, unknown>).filter(([key]) => key !== 'checkId'))) };
};

/** The field `OrderImported` v7 added: a reader of v6 takes the order without its tab. */
const orderAsV6 = (newer: unknown): unknown => Object.fromEntries(Object.entries(newer as Record<string, unknown>).filter(([key]) => key !== 'tab'));

/** The field `OrderImported` v6 added to each line: a reader of v5 takes the lines without it. */
const orderAsV5 = (newer: unknown): unknown => {
  const o = newer as { lines?: unknown };
  if (!Array.isArray(o.lines)) return newer;
  return { ...o, lines: o.lines.map((l: unknown) => Object.fromEntries(Object.entries(l as Record<string, unknown>).filter(([key]) => key !== 'modifiers'))) };
};

export const OrderImported = defineEvent({
  ...history,
  name: 'OrderImported',
  version: 24,
  summary: 'A legacy order imported as one snapshot with its stored totals (rulesVersion legacy, never repriced).',
  aggregate: 'order',
  payload: OrderImportedV3.extend({ pricing: ImportedPricing.nullable(), tab: ImportedTab.optional(), checks: ImportedChecks.optional(), online: ImportedOnline.optional(),
    /**
     * v13 (lane D2, carry-over tabs): legacy `orders.gratuity`, the gratuity percent the register reapplies to the cart, as
     * a Rate. Present only when legacy's percent is above zero; absent on a v13 fact means none (and before v13, unknown).
     */
    gratuityRate: Rate.optional(),
    /**
     * v16 (lane M round 27): the floor-plan table the order's tab sat at (legacy `tabs.floor_plan_item_id`, as
     * `Order::floorPlanItem()` reads it through the tab), the imported floor item, as a live order's `tableId`. Legacy shows
     * its number and zone in Order Lookup, the order detail and Employee Sales. Absent: no tab, no table, a table that no
     * longer exists or wasn't imported, or a fact before v16.
     */
    tableId: TableId.optional(),
    /**
     * v20 (lane M round 31, for D2's receipts): the tab's customer as legacy's tender receipt prints it, on the live
     * order's `OrderCustomer` shape (`TenderOrderResource`: the tab's customer's `mobile`, and the tab's own address,
     * else the customer's default, else its first, as `address` and "city, state zip"). Personal data. Absent: no tab, no
     * customer on it, a customer legacy deleted, or a fact before v20.
     */
    customer: OrderCustomer.optional(),
    /**
     * v21 (lane P5, WF-25): legacy `orders.auto_gratuity_status` when it was not `none`. A carried-over tab keeps the
     * automatic gratuity as the automatic one (`applied`), or records it applied and removed (`removed`), so the register
     * never puts back one staff took off. Absent: none, or a fact before v21.
     */
    autoGratuityStatus: z.enum(['applied', 'removed']).optional(),
    /**
     * v22 (lane X, B1656): the order's customer as legacy keys its customer pages (`orders.customer_id`;
     * GetCustomerDetailAction and the Orders tab's lookup read every order by it), as the imported customer. It is set on
     * register orders with no tab customer too, which `customer` (the tab's) misses. Absent: none, a customer the import
     * did not bring over (flagged `order_customer_not_imported` or `order_customer_deleted`), or a fact before v22.
     */
    customerId: CustomerId.optional() }),
  verifiable: ['orderId', 'storedTotals', 'lifecycle', 'businessDate'],
  upcastFrom: [
    // v1 carried no dining option: read as none.
    {
      version: 1,
      up: (old) => {
        OrderImportedV1.parse(old);
        return { ...(old as z.input<typeof OrderImportedV1>), diningOptionId: null };
      },
    },
    // v2's cart discount named no rule: read as unlinked.
    {
      version: 2,
      up: (old) => {
        OrderImportedV2.parse(old);
        const v2 = old as z.input<typeof OrderImportedV2>;
        return { ...v2, cartDiscount: v2.cartDiscount === null ? null : { ...v2.cartDiscount, discountRuleId: null } };
      },
    },
    // v3 carried no program or tax rate: read as not stamped.
    {
      version: 3,
      up: (old) => {
        OrderImportedV3.parse(old);
        return { ...(old as z.input<typeof OrderImportedV3>), pricing: null };
      },
    },
    // v4 lines named no variant and cart discounts no percent: still valid, read as unknown.
    additive(4),
    // v5 lines carried no modifiers: still valid, read as none recorded.
    additive(5),
    // v6 carried no tab: still valid, read as none recorded.
    additive(6),
    // v7 carried no checks: still valid, read as not split.
    additive(7),
    // v8 lines carried no stored tax apart from their tax: still valid, read as stored = tax.
    additive(8),
    // v9 lines carried no price-adjustment note: still valid, read as none recorded.
    additive(9),
    // v10 misc lines named no printers: still valid, read as the printers preselected for misc items.
    additive(10),
    // v11 carried no online record: still valid, read as none recorded.
    additive(11),
    // v12 carried no gratuity rate or entered bases: still valid, read as not recorded.
    additive(12),
    // v13 lines carried no voids: still valid, read as none recorded.
    additive(13),
    // v14 lines carried no cash-price mark: still valid, read as rung at the listed price.
    additive(14),
    // v15 carried no table: still valid, read as none recorded.
    additive(15),
    // v16 lines carried no stored subtotal apart from their gross: still valid, read as stored = gross.
    additive(16),
    // v17 lines carried no sent units: still valid, read as not recorded (a carry-over takes every unit as sent).
    additive(17),
    // v18 lines carried no product-name mark: still valid, read as the line's own name.
    additive(18),
    // v19 carried no customer: still valid, read as none recorded.
    additive(19),
    // v20 carried no auto-gratuity status: still valid, read as none.
    additive(20),
    // v21 carried no customer reference: still valid, read as none recorded.
    additive(21),
    // v22 marked no line's stored name '0': still valid, read as none (its lines group under the blank row).
    additive(22),
    // v23 marked no line removed from its order: still valid, read as kept (Item Sales' variant rows may read it).
    additive(23),
  ],
  // A node on a build that reads v23, v22, v21, v20, v19, v18, v17, v16, v15, v14, v13, v12, v11, v10, v9, v8, v7, v6 or v5 keeps hydrating its history: the order loses only its lines' removed marks (then their stored-'0' marks, then its customer reference, then its auto-gratuity status, then its customer, then its lines' product-name marks, then their sent units, then their stored subtotals, then its table, then its lines' cash-price marks, then its lines' voids, then its gratuity rate and entered discount bases, then its online record, then its misc lines' printers, then their price-adjustment notes, then their stored tax, its checks, its tab, its line modifiers).
  downcastTo: [{ version: 23, down: orderAsV23 }, { version: 22, down: orderAsV22 }, { version: 21, down: orderAsV21 }, { version: 20, down: orderAsV20 }, { version: 19, down: orderAsV19 }, { version: 18, down: orderAsV18 }, { version: 17, down: orderAsV17 }, { version: 16, down: orderAsV16 }, { version: 15, down: orderAsV15 }, { version: 14, down: orderAsV14 }, { version: 13, down: orderAsV13 }, { version: 12, down: orderAsV12 }, { version: 11, down: orderAsV11 }, { version: 10, down: orderAsV10 }, { version: 9, down: orderAsV9 }, { version: 8, down: orderAsV8 }, { version: 7, down: orderAsV7 }, { version: 6, down: orderAsV6 }, { version: 5, down: orderAsV5 }],
  pii: ['customer.name', 'customer.phone', 'customer.address1', 'customer.address2'],
});

/** The fields `TenderImported` v2 added: a reader of v1 takes the payload without them. */
const TENDER_V2_FIELDS = new Set(['processor', 'processorRef', 'houseAccountCustomerId', 'cardLast4']);
/** The field `TenderImported` v3 added: a reader of v2 takes the payload without it. */
/** The field `TenderImported` v4 added: a reader of v3 takes the tender without it. */
/** The fields `TenderImported` v5 added: a reader of v4 takes the tender without them. */
const TENDER_V5_FIELDS = new Set(['tendered', 'changeRounding']);
/** The fields `TenderImported` v6 added (B1624): a reader of v5 takes the tender without them. */
const TENDER_V6_FIELDS = new Set(['cardBrand', 'tenderTypeName', 'details']);
/** The field `TenderImported` v7 added (lane BL, B1654): a reader of v6 takes the tender without it. */
const tenderAsV6 = (newer: unknown): unknown => Object.fromEntries(Object.entries(newer as Record<string, unknown>).filter(([key]) => key !== 'thirdPartySource'));
const tenderAsV5 = (newer: unknown): unknown => Object.fromEntries(Object.entries(tenderAsV6(newer) as Record<string, unknown>).filter(([key]) => !TENDER_V6_FIELDS.has(key)));
const tenderAsV4 = (newer: unknown): unknown => Object.fromEntries(Object.entries(tenderAsV5(newer) as Record<string, unknown>).filter(([key]) => !TENDER_V5_FIELDS.has(key)));
const tenderAsV3 = (newer: unknown): unknown => Object.fromEntries(Object.entries(tenderAsV4(newer) as Record<string, unknown>).filter(([key]) => key !== 'tipFinalized'));
const tenderAsV2 = (newer: unknown): unknown => Object.fromEntries(Object.entries(newer as Record<string, unknown>).filter(([key]) => key !== 'checkId'));
const tenderAsV1 = (newer: unknown): unknown =>
  Object.fromEntries(Object.entries(newer as Record<string, unknown>).filter(([key]) => !TENDER_V2_FIELDS.has(key)));

export const TenderImported = defineEvent({
  ...history,
  name: 'TenderImported',
  version: 7,
  summary: 'A legacy tender imported with its stored amount, tip and fee (cash discount or surcharge), and what a refund of it needs.',
  aggregate: 'tender',
  payload: z.object({
    tenderId: TenderId,
    orderId: OrderId,
    locationId: LocationId,
    legacyTenderId: LegacyId,
    method: z.enum(['cash', 'card', 'gift', 'check', 'house', 'other', 'loyalty']),
    tenderState: z.enum(['captured', 'authorized', 'voided']),
    amount: Money,
    tip: Money,
    cashDiscount: Money,
    surcharge: Money,
    tillSessionId: TillSessionId.nullable(),
    receiptTicket: z.string().max(255).nullable(),
    takenAt: EpochMs,
    businessDate: LocalDate,
    flags: Flags,
    // v2 (plan 2026-10-13-refund-imported-orders, item 2): what a refund of the tender on Radish Next needs. Absent on v1
    // facts. A card token (legacy `transaction_card_events.token`) is never imported: the cloud looks it up by RefNo.
    /** The location's card processor (legacy `locations.gateway`, which legacy's refund uses); null for a non-card tender or another gateway. */
    processor: z.enum(['datacap', 'clearent']).nullable().optional(),
    /** What legacy's refund names the payment by: `transaction_id2 ?? transaction_id` (Datacap RefNo, Clearent transaction id). */
    processorRef: z.string().min(1).max(64).nullable().optional(),
    /** A house-account tender's customer (legacy `transaction_id`), when the import brought the customer over. */
    houseAccountCustomerId: CustomerId.nullable().optional(),
    /** A card's last four (legacy `cc_last_four`), kept in the tender summary only as the location's keyed digest. */
    cardLast4: z.string().regex(/^\d{4}$/).nullable().optional(),
    /** v3: the check it paid on a split order (legacy `tenders.t`, the check's index), one of its `OrderImported` checks. Absent: not split, or before v3. */
    checkId: CheckId.optional(),
    /**
     * v4 (lane D4, payroll tips): legacy `tenders.complete`, true once a card's tip is finalized (an auth-and-tip card
     * after Finalize Tips, a sale-mode card at once) and on every non-card tender. Legacy's payroll Tips and the register's
     * Tip Payout count a card tip only when it is (`type = 'card' AND complete AND tip > 0`). Absent: a fact before v4.
     */
    tipFinalized: z.boolean().optional(),
    /**
     * v5 (B1091, legacy cash change rounding): what the customer handed over on a cash tender (legacy
     * `tenders.tendered_amount`, stored only while legacy's FEATURE_CASH_CHANGE_ROUNDING_TENDER_COLUMNS is on), so a
     * reprint can show the change. Absent: legacy stored none, or a fact before v5.
     */
    tendered: Money.optional(),
    /**
     * v5: the signed cash change rounding legacy stored on the tender (`tenders.cash_change_rounding_amount`, exact change
     * less the change handed back; positive when the drawer kept more), as the live `payments.takeCash` carries it.
     * Absent when legacy stored none or zero. History only: the imported till session's expected cash already counts it.
     */
    changeRounding: Money.optional(),
    /**
     * v6 (B1624): what legacy names the payment by. A card's brand as its processor wrote it (legacy
     * `transaction_card_details.card_type`, "VISA", "M/C"; kept since 2026-03), read by core `tenderLabel` ("Visa x4242").
     * Absent: legacy kept none (older cards, most of them), not a card, or a fact before v6; the label reads "Card".
     */
    cardBrand: z.string().min(1).max(20).optional(),
    /** v6: an other tender's custom tender type name (legacy `tender_types.name`, a deleted type's too). Absent: untyped, or before v6. */
    tenderTypeName: z.string().min(1).max(255).optional(),
    /**
     * v6: an other tender's tracking detail (legacy `transaction_id` on an `other` tender: "DoorDash #A1B2C3"), and (B1922,
     * same field, since 2026-11-01) a check's number (`transaction_id` on a check: "Check #1234"). Absent: none entered,
     * before v6, or a check imported before B1922 (it reads "Check" until its tender is imported again).
     */
    details: z.string().min(1).max(255).optional(),
    /**
     * v7 (lane BL, B1654; legacy OLO-167): the delivery app that took a card's money (legacy `tenders.third_party_source`,
     * "stream:uber", a Stream DSP order's prepaid tender, flagged `third_party_prepaid`). Legacy's reports file it on the
     * app's own line under other, never under card, and charge no True Margin fee on its tip. Absent: not a delivery-app
     * tender, or a fact before v7.
     */
    thirdPartySource: z.string().max(255).optional(),
  }),
  verifiable: ['tenderId', 'amount', 'tip', 'tenderState'],
  // `details` is declared personal data: free text staff typed (a delivery order number, usually, but it could name a customer).
  pii: ['cardLast4', 'details'],
  upcastFrom: [additive(1), additive(2), additive(3), additive(4), additive(5), additive(6)],
  // A node on a build that reads v6, v5, v4, v3, v2 or v1 keeps hydrating its history: the tender loses only its delivery app (then its card brand, tender type name and detail, then its tendered amount and change rounding, then whether its tip was finalized, then its check, then its refund fields).
  downcastTo: [{ version: 6, down: tenderAsV6 }, { version: 5, down: tenderAsV5 }, { version: 4, down: tenderAsV4 }, { version: 3, down: tenderAsV3 }, { version: 2, down: tenderAsV2 }, { version: 1, down: tenderAsV1 }],
});

const RefundMethod = z.enum(['cash', 'card', 'gift', 'check', 'house', 'other', 'loyalty']);

const RefundImportedV1 = z.object({
  refundId: z.uuid({ version: 'v7' }), orderId: OrderId, locationId: LocationId, legacyRowId: LegacyId.nullable(),
  mode: z.enum(['items', 'custom_amount', 'tips_only']), method: z.enum(['cash', 'card', 'gift', 'check', 'house', 'other', 'loyalty', 'mixed']),
  amount: Money, tips: Money, refundedAt: EpochMs, businessDate: LocalDate, orderBusinessDate: LocalDate, employeeId: EmployeeId.nullable(),
  tillSessionId: TillSessionId.nullable(), flags: Flags,
});

/** How a refund went back, per tender method (legacy `tender_refunds`): what each method returned, and its tip part. */
const ImportedRefundTender = z
  .object({ refundTenderId: z.uuid({ version: 'v7' }), method: RefundMethod, amount: Money, tip: Money })
  .meta({ id: 'ImportedRefundTender' });

/**
 * v4: what the refund took off one payment of the sale (legacy `tender_refunds` of that tender, summed), so a later
 * refund on Radish Next cannot give the same money back twice (legacy `OrderRefundDetailsService`: a tender's amount
 * less Σ `amount_refund`, its tip less Σ `tip_refund`).
 */
const ImportedRefundedTender = z
  .object({ tenderId: TenderId, method: RefundMethod, amount: Money, tip: Money })
  .meta({ id: 'ImportedRefundedTender' });

/**
 * v4: what the refund took off one line of the sale (legacy `order_item_refunds` of that line): the number of rows,
 * which is how legacy counts refunded units (`RefundAllocationService::refundedUnits`, capped at the line's quantity),
 * and Σ their `amount` (tax included), which legacy nets off the pool later item refunds share.
 */
const ImportedRefundedRow = z
  .object({ quantity: z.int().min(-100_000).max(100_000), amount: Money })
  .meta({ id: 'ImportedRefundedRow' });

const ImportedRefundedLine = z
  .object({
    lineId: OrderLineId, units: z.int().nonnegative().max(100_000), amount: Money,
    /**
     * v5: the line's `order_item_refunds` rows legacy's Refund Details lists on this refund, each on its own, oldest first
     * (an `order_refunds` refund lists only the rows naming it); absent on imports that predate v5.
     */
    rows: z.array(ImportedRefundedRow).max(100).optional(),
    /**
     * v6: the sale line's tax as legacy stored it (`order_items.tax`), which legacy's Refund Details takes one unit of per
     * row. It differs from the imported line's `tax` when the import allocated that from `orders.tax` (`taxAllocated`);
     * absent on imports that predate v6 (read the line's `tax`).
     */
    saleLineTax: Money.optional(),
  })
  .meta({ id: 'ImportedRefundedLine' });

/**
 * v8 (B1676): one return line legacy rang on a closed ticket (`order_items` with `quantity < 0` on a `complete` or `paid`
 * order), which the import moves out of the sale into its `order_return` refund (L132). Kept as legacy stored it, so the
 * customer page's Favorite Items nets it as legacy's signed `SUM(quantity)` and `SUM(subtotal)` do.
 */
const ImportedReturnedLine = z
  .object({
    /** The name legacy's reports give the line (`$item->name ?: $item->product?->name`). */
    name: z.string().max(255),
    /**
     * Legacy stored no name of its own on the line (`order_items.name` NULL, empty or '0'), so `name` is its product's.
     * Legacy's Favorite Items groups such lines into its blank row, not under the product (B1684), except '0'.
     */
    nameFromProduct: z.literal(true).optional(),
    /** Legacy stored the name '0', which its Favorite Items groups as a row of its own (as OrderImported v23's lines). */
    storedNameZero: z.literal(true).optional(),
    productId: ProductId.nullable(),
    /** The units returned, positive (legacy's `-quantity`), in thousandths. */
    qtyMilli: Milli,
    /** Legacy's `order_items.subtotal` as stored, signed (negative on a return). */
    subtotal: Money,
  })
  .meta({ id: 'ImportedReturnedLine' });

/** The fields `RefundImported` v4 added: a reader of v3 takes the payload without them. */
const REFUND_V4_FIELDS = new Set(['refundedTenders', 'refundedLines']);
/** The field `RefundImported` v8 added: a reader of v7 takes the refund without it. */
const refundAsV7 = (newer: unknown): unknown => Object.fromEntries(Object.entries(newer as Record<string, unknown>).filter(([key]) => key !== 'returnedLines'));
/** The fields `RefundImported` v7 and v8 added: a reader of v6 takes the refund without them. */
const refundAsV6 = (newer: unknown): unknown => Object.fromEntries(Object.entries(refundAsV7(newer) as Record<string, unknown>).filter(([key]) => key !== 'changeRounding'));
const refundAsV3 = (newer: unknown): unknown =>
  Object.fromEntries(Object.entries(refundAsV6(newer) as Record<string, unknown>).filter(([key]) => !REFUND_V4_FIELDS.has(key)));

/** A reader of an older version takes each refunded line without the fields added after it. */
const withoutLineFields = (newer: unknown, fields: ReadonlySet<string>): unknown => {
  const o = newer as { refundedLines?: unknown };
  if (!Array.isArray(o.refundedLines)) return newer;
  return { ...o, refundedLines: o.refundedLines.map((l: unknown) => Object.fromEntries(Object.entries(l as Record<string, unknown>).filter(([key]) => !fields.has(key)))) };
};
/** The field `RefundImported` v6 added to each refunded line: a reader of v5 takes the lines without the sale line's stored tax. */
const refundAsV5 = (newer: unknown): unknown => withoutLineFields(refundAsV6(newer), new Set(['saleLineTax']));
/** The fields v5 and v6 added to each refunded line: a reader of v4 takes the lines without their rows or stored tax. */
const refundAsV4 = (newer: unknown): unknown => withoutLineFields(refundAsV6(newer), new Set(['rows', 'saleLineTax']));

export const RefundImported = defineEvent({
  ...history,
  name: 'RefundImported',
  version: 8,
  summary: 'A legacy refund imported on its own business date, linked to the sale it refunds, with its tax split out and its tenders.',
  aggregate: 'order',
  payload: z.object({
    refundId: z.uuid({ version: 'v7' }),
    /** The sale the refund belongs to, and that sale's business date (the refund itself reports on `businessDate`). */
    orderId: OrderId,
    locationId: LocationId,
    /** The legacy `order_refunds` row; null for a legacy cash refund recorded as a negative tender. */
    legacyRowId: LegacyId.nullable(),
    mode: z.enum(['items', 'custom_amount', 'tips_only']),
    method: z.enum(['cash', 'card', 'gift', 'check', 'house', 'other', 'loyalty', 'mixed']),
    /** What was refunded, tax included and tips excluded (legacy `order_refunds.amount`). */
    amount: Money,
    /** `amount` split once at import: the tax part (the order's stored tax share of its total), and the rest. */
    net: Money,
    tax: Money,
    tips: Money,
    tenders: z.array(ImportedRefundTender).max(20),
    refundedAt: EpochMs,
    businessDate: LocalDate,
    orderBusinessDate: LocalDate,
    employeeId: EmployeeId.nullable(),
    tillSessionId: TillSessionId.nullable(),
    flags: Flags,
    /** Legacy `order_refunds.comment` (the Refunds report's Comments); absent on imports that predate it. */
    comment: z.string().max(1000).nullable().optional(),
    /** Σ legacy `order_item_refunds.quantity` in thousandths (the Refunds report's Items); absent on imports that predate it. */
    itemsQtyMilli: Milli.optional(),
    // v4 (fix before cutover: a legacy refund must count against later refunds of the imported order). Absent on v3 facts.
    /** Per payment of the sale: what this refund took off it. */
    refundedTenders: z.array(ImportedRefundedTender).max(50).optional(),
    /** Per line of the sale: the refunded units and amount. */
    refundedLines: z.array(ImportedRefundedLine).max(500).optional(),
    /**
     * v7 (B1091): the signed cash change rounding legacy stored on the refund's hand-back
     * (`order_refunds.cash_change_rounding_amount`, exact cash refunded less the cash handed back), as the live
     * `RequestRefund` carries it. Absent when legacy stored none or zero. History only: the imported till session's
     * expected cash already counts it.
     */
    changeRounding: Money.optional(),
    /**
     * v8 (B1676): on an `order_return` refund (`legacyRowId` null, the returns rung on a closed ticket), each return line
     * as legacy stored it. Absent on any other refund and on imports that predate v8. History only: the refund's money is
     * `amount`, `net` and `tax`, as before.
     */
    returnedLines: z.array(ImportedReturnedLine).max(500).optional(),
  }),
  verifiable: ['refundId', 'amount', 'tax', 'tips'],
  // v1 (the first local proof runs) carried no tax split or tenders: read as all net, one tender of its method.
  upcastFrom: [{
    version: 1,
    up: (old) => {
      RefundImportedV1.parse(old);
      const v1 = old as z.input<typeof RefundImportedV1>;
      const method = v1.method === 'mixed' ? 'other' : v1.method;
      return {
        ...v1, net: v1.amount, tax: { minor: '0', currency: v1.amount.currency },
        tenders: [{ refundTenderId: v1.refundId, method, amount: v1.amount, tip: v1.tips }],
      };
    },
  },
  // v2 carried no comment or item count (D5, the Refunds report's Comments and Items): read as absent.
  additive(2),
  // v3 carried no per-payment or per-line rows: read as absent (nothing known to have been taken off any one payment).
  additive(3),
  // v4 carried no rows per refunded line (D5, legacy's Refund Details lists each row): read as absent.
  additive(4),
  // v5 carried no stored tax per refunded line (D5, location 66: legacy's Refund Details reads `order_items.tax`): read as absent.
  additive(5),
  // v6 carried no change rounding (B1091): read as handed back exact.
  additive(6),
  // v7 carried no returned lines (B1676): read as absent (the refund's money is unchanged).
  additive(7)],
  // A node on a v7 or older build keeps hydrating its history: the refund loses only its returned lines (then its
  // change rounding, then its per-line rows and stored tax, then its per-payment and per-line detail).
  downcastTo: [{ version: 7, down: refundAsV7 }, { version: 6, down: refundAsV6 }, { version: 5, down: refundAsV5 }, { version: 4, down: refundAsV4 }, { version: 3, down: refundAsV3 }],
});

const ImportedCashMovement = z
  .object({
    movementId: CashMovementId,
    legacyId: LegacyId,
    /** Legacy `cash_managements.type`, kept as written (`paid-in`, `tip-payout`, …). */
    legacyKind: z.string().max(64),
    /** Signed effect on the drawer's expected cash, by the legacy `Drawer` sign table. */
    signedAmount: Money,
    employeeId: EmployeeId.nullable(),
    at: EpochMs,
    /**
     * A tip payout's recipient (legacy `tip_employee_id`) and the amount legacy stored, which payroll's tips read when
     * they come from payouts (`Employee::tipPayout`); null for other movements. Absent before v2.
     */
    tipEmployeeId: EmployeeId.nullable().optional(),
    amount: Money.optional(),
    /**
     * v3: the server bank a settlement on a register drawer settles (legacy `cash_managements.server_bank_id`), which the
     * drawer's Settlements (Server Banks) table lists; null when that bank was not imported. Absent: legacy named none.
     */
    counterpartyTillSessionId: TillSessionId.nullable().optional(),
    /**
     * v4: the description staff typed (legacy `cash_managements.note`, varchar(255)), which the drawer detail's Cash
     * Management table shows in its Note column. Free text: never logged. Absent: legacy kept none, or before v4.
     */
    note: z.string().max(255).optional(),
  })
  .meta({ id: 'ImportedCashMovement' });

/** One part of an approved server-bank closeout: where its cash or tips went, and which manager approved it. */
const ImportedCloseoutPart = z
  .object({
    /** Legacy `payment_type` / `tip_payment_type`: `cash_drawer` (a register drawer) or `deposit` (the safe). */
    destination: z.enum(['cash_box', 'deposit']),
    /** The register drawer's till session (legacy `till_drawer_id` / `tip_till_drawer_id`); null for the safe, or a drawer not imported. */
    destinationTillSessionId: TillSessionId.nullable(),
    approvedByEmployeeId: EmployeeId.nullable(),
  })
  .meta({ id: 'ImportedCloseoutPart' });

/**
 * A server bank's closeout as legacy's `drawer_closure_intents` row holds it (the newest when there are several). A
 * pending or rejected intent imports as submitted and unsettled: statuses unpaid, no approval, no parts.
 */
const ImportedBankCloseout = z
  .object({
    legacyIntentId: LegacyId,
    intentState: z.enum(['pending', 'approved', 'rejected']),
    submittedAt: EpochMs,
    isNextDay: z.boolean(),
    /** The bank's server (legacy `employee_id`), when imported. */
    serverEmployeeId: EmployeeId.nullable(),
    figures: z.object({ cashCollected: Money, nonCashTips: Money, gratuity: Money, orderCount: z.int().nonnegative() }),
    cash: z.enum(['unpaid', 'collected', 'paid']),
    tips: z.enum(['unpaid', 'paid']),
    /** Settled together (legacy `has_separate_tips = 0`). */
    combined: z.boolean(),
    /** Legacy keeps only the last approval's time. */
    approvedAt: EpochMs.nullable(),
    cashPart: ImportedCloseoutPart.nullable(),
    tipPart: ImportedCloseoutPart.nullable(),
    notes: z.string().max(255).nullable(),
  })
  .meta({ id: 'ImportedBankCloseout' });

/** The fields `TillSessionImported` v3 added: a reader of v2 takes the payload without them. */
const without = (row: Record<string, unknown>, key: string): Record<string, unknown> => Object.fromEntries(Object.entries(row).filter(([k]) => k !== key));
const tillAsV2 = (newer: unknown): unknown => {
  const till = without(newer as Record<string, unknown>, 'closeout');
  const movements = till['movements'];
  return Array.isArray(movements) ? { ...till, movements: movements.map((m: Record<string, unknown>) => without(without(m, 'counterpartyTillSessionId'), 'note')) } : till;
};
/** The field `TillSessionImported` v4 added (a movement's typed note): a reader of v3 takes the payload without it. */
const tillAsV3 = (newer: unknown): unknown => {
  const till = newer as Record<string, unknown>;
  const movements = till['movements'];
  return Array.isArray(movements) ? { ...till, movements: movements.map((m: Record<string, unknown>) => without(m, 'note')) } : till;
};

export const TillSessionImported = defineEvent({
  ...history,
  name: 'TillSessionImported',
  version: 4,
  summary: 'A legacy drawer imported as a till session, with its expected cash computed once by the legacy formula.',
  aggregate: 'till',
  payload: z.object({
    tillSessionId: TillSessionId,
    locationId: LocationId,
    legacyDrawerId: LegacyId,
    legacyKind: z.string().max(32),
    letter: z.string().max(16),
    deviceId: DeviceId.nullable(),
    openedByEmployeeId: EmployeeId.nullable(),
    closedByEmployeeId: EmployeeId.nullable(),
    businessDate: LocalDate,
    openedAt: EpochMs,
    closedAt: EpochMs.nullable(),
    tillState: z.enum(['open', 'closed', 'auto_closed']),
    openingFloat: Money,
    /** null when legacy recorded no count (auto-closed with 0, CD-C09). */
    counted: Money.nullable(),
    cashSales: Money,
    cashRefunds: Money,
    expectedCash: Money,
    movements: z.array(ImportedCashMovement).max(5_000),
    flags: Flags,
    /** v3: a server bank's closeout (plans/2026-10-11-parity-program/d5-bank-closeout-import.md). Absent: not a bank, no intent, or before v3. */
    closeout: ImportedBankCloseout.optional(),
  }),
  verifiable: ['tillSessionId', 'expectedCash', 'counted'],
  // A movement's note is free text staff typed and may name people (coordinator 2026-10-15).
  pii: ['movements[].note'],
  // v1 movements named no tip payout recipient nor stored amount, v2 facts no closeout nor counterparty, v3 movements no
  // note: still valid, read as unknown.
  upcastFrom: [additive(1), additive(2), additive(3)],
  downcastTo: [{ version: 3, down: tillAsV3 }, { version: 2, down: tillAsV2 }],
});

const ImportedBreak = z
  .object({
    legacyId: LegacyId,
    startedAt: EpochMs,
    endedAt: EpochMs.nullable(),
    policyName: z.string().max(255).nullable(),
    minutes: z.int().nonnegative(),
    paid: z.boolean(),
    /** An excess break (legacy `break_id = 0`) points at the break it overran. */
    overrunOfLegacyId: LegacyId.nullable(),
    /**
     * v3: the legacy state of the break's definition: `live`, `deleted` (soft-deleted before the import) or `missing` (no
     * row). Legacy ignores a break under a deleted or missing definition for hours and meals (B416, B418). Absent on an
     * excess break and on facts before v3, read as live.
     */
    definition: z.enum(['live', 'deleted', 'missing']).optional(),
    /** v3: the imported definition's id in Radish Next (a live definition), so retiring it later reaches this break. */
    breakPolicyId: BreakPolicyId.optional(),
    /**
     * v5 (B75): legacy's own row flag `employee_break_takens.pay_for_buffer`, true only on a break legacy added in a
     * back-office Update of a definition that pays its buffer. A later adjust in 'legacy' mode reads it for the paid
     * allowance. Absent on an excess break and on facts before v5, read as false (the column's default).
     */
    paysBuffer: z.boolean().optional(),
  })
  .meta({ id: 'ImportedBreak' });

const TimecardImportedV1 = z.object({
  timecardId: TimecardId,
  employeeId: EmployeeId,
  locationId: LocationId,
  legacyTimecardId: LegacyId,
  businessDate: LocalDate,
  clockedInAt: EpochMs,
  clockedOutAt: EpochMs.nullable(),
  autoClosedAt: EpochMs.nullable(),
  breaks: z.array(ImportedBreak).max(50),
  /** The employee's legacy wage at import (current-wage semantics, platform migration-notes §6.2). */
  wageSnapshot: Money,
  deleted: z.boolean(),
  flags: Flags,
});

/**
 * One correction legacy logged on the card (`timecard_events`): which time, its old and new value, when, by which legacy
 * user and with what note. The new value can be a day off (bug B8); the card's own times are the truth.
 */
const ImportedCorrection = z
  .object({
    legacyId: LegacyId,
    field: z.enum(['clock_in', 'clock_out', 'break_start', 'break_end']),
    /** The `employee_break_takens` row a break correction changed. */
    breakLegacyId: LegacyId.nullable(),
    from: EpochMs,
    to: EpochMs,
    at: EpochMs,
    note: z.string().max(500).nullable(),
    /** Legacy's `updated_by` (a back-office user). */
    legacyUserId: LegacyId.nullable(),
    /** v4 (lane M round 29, D4's Edit Log): the editor as an imported user. Absent: no editor, or one not imported. */
    editedByUserId: UserId.optional(),
    /**
     * v4: legacy's display name of an editor who is not an imported user (former staff, a deleted user, Radish staff), as
     * legacy's Edit Log names them; personal data. Absent: no editor, an imported one, or one legacy can no longer name.
     */
    editedByName: z.string().min(1).max(255).optional(),
  })
  .meta({ id: 'ImportedCorrection' });

/** A v5 `TimecardImported` as v4 readers know it: each break without `paysBuffer`. */
function timecardAsV4(newer: unknown): unknown {
  const card = newer as Record<string, unknown>;
  const breaks = card['breaks'];
  return Array.isArray(breaks) ? { ...card, breaks: breaks.map((b: Record<string, unknown>) => without(b, 'paysBuffer')) } : card;
}

/** A v4 `TimecardImported` as v3 readers know it: each correction without its editor. */
function timecardAsV3(newer: unknown): unknown {
  const card = newer as Record<string, unknown>;
  const corrections = card['corrections'];
  return Array.isArray(corrections) ? { ...card, corrections: corrections.map((c: Record<string, unknown>) => without(without(c, 'editedByUserId'), 'editedByName')) } : card;
}

/** A v3 `TimecardImported` as v2 readers know it: each break without `definition` and `breakPolicyId`. */
function timecardAsV2(newer: unknown): unknown {
  const card = newer as Record<string, unknown>;
  const breaks = card['breaks'];
  return Array.isArray(breaks) ? { ...card, breaks: breaks.map((b: Record<string, unknown>) => without(without(b, 'definition'), 'breakPolicyId')) } : card;
}

export const TimecardImported = defineEvent({
  ...history,
  name: 'TimecardImported',
  version: 5,
  summary: "A legacy timecard imported with its punches and breaks as they stand (corrections already applied) and legacy's log of those corrections.",
  aggregate: 'timecard',
  payload: TimecardImportedV1.extend({ corrections: z.array(ImportedCorrection).max(500) }),
  verifiable: ['timecardId', 'clockedInAt', 'clockedOutAt'],
  // v1 carried no corrections: read as none logged. v2 breaks named no definition state nor policy id: read as live.
  upcastFrom: [{
    version: 1,
    up: (old) => {
      TimecardImportedV1.parse(old);
      return { ...(old as z.input<typeof TimecardImportedV1>), corrections: [] };
    },
  }, additive(2), additive(3), additive(4)],
  // A reader on v4 gets the breaks without `paysBuffer`; one on v3 also the corrections without their editor; one on v2
  // also the breaks without the v3 fields.
  downcastTo: [
    { version: 4, down: timecardAsV4 },
    { version: 3, down: timecardAsV3 },
    { version: 2, down: timecardAsV2 },
  ],
  pii: ['corrections[].editedByName'],
});

/**
 * A legacy gift-card transaction (`gift_card_transactions`) imported as history for legacy's Gift Cards report (lane M
 * round 29 with D5; coordinator option a, 2026-10-15): one fact per legacy row, on its own stream, never on the card's
 * ledger (`gift_card:<id>`), so it never moves a balance; the card's balance stays legacy's (`giftCards.importLegacyCard`).
 * Written to the location of the row's employee (legacy's report filters `employees.location_id`), soft-deleted rows
 * included (the report keeps them). No card number: its last four digits and its length (L176), the bearer secret never.
 */
export const GiftCardTransactionImported = defineEvent({
  ...history,
  name: 'GiftCardTransactionImported',
  version: 1,
  summary: "A legacy gift-card load or redemption imported as history for the Gift Cards report; it never moves a card's balance.",
  aggregate: 'gift_card_transaction',
  payload: z.object({
    transactionId: z.uuid({ version: 'v7' }),
    legacyTransactionId: LegacyId,
    /** The imported card; null when legacy's card is not imported (soft-deleted). */
    giftCardId: GiftCardId.nullable(),
    legacyGiftCardId: LegacyId,
    /**
     * The last four characters as legacy stored the number (trimmed): legacy took numbers outside 12-20 digits too (the
     * card import flags `card_number_nonstandard`), so any character is kept as legacy's report would show it.
     */
    cardLast4: z.string().max(4),
    cardNumberLength: z.int().nonnegative().max(64),
    /** Legacy `type`: `loading`, `redeemed`, or anything else (a clear row's null) as `other`. */
    kind: z.enum(['loading', 'redeemed', 'other']),
    amount: Money,
    /** `gift_card_transactions.created_at`: legacy's report filters both lists by it, and lists loads by it. */
    at: IsoInstant,
    deletedAt: IsoInstant.nullable(),
    /** The imported employee (every legacy employee of the location is imported, deleted ones as inactive). */
    employeeId: EmployeeId,
    /** A redemption's tender as legacy's report joins it: its amount, its time (the report lists and orders by it) and ticket. Absent: no tender row. */
    redemption: z.object({ tenderId: TenderId.nullable(), amount: Money, at: IsoInstant, ticket: z.int().nullable() }).optional(),
  }),
  verifiable: ['transactionId', 'amount', 'kind'],
  pii: ['cardLast4'],
});

/** Every legacy import fact, in the order the importer writes them. */
export const legacyImportEvents = [OrderImported, TenderImported, RefundImported, TillSessionImported, TimecardImported, GiftCardTransactionImported] as const;
